<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { routes } from '@app/router/routes'

import Drawer from '@uiKit/elements/Drawer.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

import InvestPanel, { type ConfirmedOrder, type InvestStep } from '../components/InvestPanel.vue'

import { OrderDocumentsContainer, usePlaceOrderMutation } from '@modules/orders'
import { fetchTermsAndConditions } from '@modules/auth'
import { getIdempotencyKey, openHtmlInNewTab } from '@shared/utils'
import { normalizeError } from '@core/api/normalizeError'

import { usePropertyDetail } from '../../property'
import { useInvestEligibility } from '../composables/useInvestEligibility'
import { useInvestQuote } from '../composables/useInvestQuote'
import { usePlacementProgress } from '../composables/usePlacementProgress'
import { toNextSteps } from '../constants/investFlow'
import { resolveOrderError } from '../constants/orderErrors'
import { formatOwnershipShare } from '../utils/investShareUtils'

// Buy-flow drawer (Figma 32590:65397 → 67141 → 67999 → 68784):
// Investment Details → Review Order → Placing Order → Order Confirmed.

const STEP_TITLES: Record<InvestStep, string> = {
  details: 'Investment Details',
  review: 'Review Order',
  placing: 'Placing Order',
  success: 'Order Confirmed',
}

interface Props {
  open: boolean
  /**
   * Property id this drawer invests in. Parent must re-key the drawer
   * (`:key="propertyId"`) when this changes between rows — `usePropertyDetail`
   * and `useInvestEligibility` snapshot their argument at mount.
   */
  propertyId: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'order-placed'): void
}>()

const router = useRouter()

const { raw, loading, error, offeringId, gallery, header, returnTiles } = usePropertyDetail(
  props.propertyId,
)
const { eligibilityFix, walletBalance } = useInvestEligibility(props.propertyId)

const { tokenCount, quote, sliderMax, tokensAvailable, isQuoting, feesError, resetToMinimum } =
  useInvestQuote(offeringId, raw)
const totalTokens = computed(() => raw.value?.totalTokens ?? 0)

// ── Steps ───────────────────────────────────────────────────────────────
const step = ref<InvestStep>('details')
const orderError = ref<string | null>(null)
const isInsufficientBalance = ref(false)
const confirmedOrder = ref<ConfirmedOrder | null>(null)
const formInstance = ref(0)

// Stable across retries of the same attempt, so a retried POST returns the
// existing order instead of debiting twice. Fresh key per drawer open.
const idempotencyKey = ref(getIdempotencyKey())

const placeOrderMutation = usePlaceOrderMutation()
const { activeStep: placingStep, run: runPlacement } = usePlacementProgress()

watch(
  () => props.open,
  (open) => {
    if (!open) return
    step.value = 'details'
    orderError.value = null
    isInsufficientBalance.value = false
    confirmedOrder.value = null
    resetToMinimum()
    formInstance.value++
    idempotencyKey.value = getIdempotencyKey()
  },
)

const goToReview = () => {
  orderError.value = null
  step.value = 'review'
}

const placeOrder = async () => {
  orderError.value = null
  isInsufficientBalance.value = false
  const tokens = tokenCount.value
  step.value = 'placing'
  try {
    const order = await runPlacement(() =>
      placeOrderMutation.mutateAsync({
        offeringId: offeringId.value,
        tokenQuantity: tokens,
        idempotencyKey: idempotencyKey.value,
        acceptedTerms: true,
      }),
    )
    const boughtTokens = order.tokenQuantity || tokens
    confirmedOrder.value = {
      id: order.id,
      orderReference: order.orderReference,
      tokens: boughtTokens,
      stakeLabel: formatOwnershipShare(boughtTokens, totalTokens.value),
      // BE-confirmed total wins over the pre-order quote.
      totalPaid: Number(order.netAmount) || quote.value.netAmount,
      nextSteps: toNextSteps(order.statusTracker),
    }
    step.value = 'success'
    // Availability changed — the host list refetches while the receipt stays open.
    emit('order-placed')
  } catch (e) {
    const resolved = resolveOrderError(normalizeError(e))
    orderError.value = resolved.message
    isInsufficientBalance.value = resolved.isInsufficientBalance
    step.value = 'review'
  }
}

const openTerms = async () => {
  try {
    const data = await fetchTermsAndConditions()
    openHtmlInNewTab(data.content)
  } catch {
    // The terms page is informational; a failed fetch leaves the flow as is.
  }
}

const close = () => emit('close')

const viewOrder = () => {
  const order = confirmedOrder.value
  if (!order) return
  router.push(routes.ORDER_DETAIL.replace(':id', props.propertyId).replace(':orderId', order.id))
  close()
}

const backToProperties = () => {
  router.push(routes.PROPERTIES)
  close()
}

const addFunds = () => {
  router.push(routes.WALLET)
  close()
}

const isPlacing = computed(() => step.value === 'placing')
</script>

<template>
  <Drawer
    :model-value="open"
    position="right"
    width="540px"
    tone="muted"
    :show-close-icon="false"
    :dismissable="!isPlacing"
    @update:model-value="(visible) => !visible && !isPlacing && close()"
  >
    <template #header>
      <Box align="center" justify="between" gap="2" block>
        <Text type="title-lg" weight="semibold" is="h2" style="line-height: 30px">{{
          STEP_TITLES[step]
        }}</Text>
        <Box
          v-if="!isPlacing"
          is="button"
          type="button"
          align="center"
          justify="center"
          class="invest-drawer__close"
          aria-label="Close"
          @click="close"
        >
          <PiIcon name="times" class="invest-drawer__close-icon" />
        </Box>
      </Box>
    </template>

    <InvestPanel
      v-model:tokens="tokenCount"
      :step="step"
      :loading="loading"
      :property-error="!!error"
      :eligibility-fix="eligibilityFix"
      :property-image="gallery.images[0] ?? ''"
      :asset-name="header.title"
      :location="header.location"
      :total-tokens="totalTokens"
      :sliceal-price="raw?.purchasePrice ?? null"
      :min-investment-amount="raw?.minInvestmentAmount ?? null"
      :max-investment-amount="raw?.maxInvestmentAmount ?? null"
      :tokens-available="tokensAvailable"
      :slider-max="sliderMax"
      :quote="quote"
      :is-quoting="isQuoting"
      :fees-error="feesError"
      :return-tiles="returnTiles"
      :wallet-balance="walletBalance"
      :order-error="orderError"
      :is-insufficient-balance="isInsufficientBalance"
      :placing-step="placingStep"
      :confirmed-order="confirmedOrder"
      :form-instance-key="formInstance"
      @eligibility-cta="eligibilityFix?.onCta()"
      @continue="goToReview"
      @place-order="placeOrder"
      @open-terms="openTerms"
      @add-funds="addFunds"
      @view-order="viewOrder"
      @back-to-properties="backToProperties"
    >
      <!-- Fresh order: the title deed lands ~30 s after clearing, so poll for it. -->
      <template #order-documents>
        <OrderDocumentsContainer
          v-if="confirmedOrder"
          :order-id="confirmedOrder.id"
          :order-reference="confirmedOrder.orderReference"
          poll-title-deed
        />
      </template>
    </InvestPanel>
  </Drawer>
</template>

<style scoped>
.invest-drawer__close {
  width: 30px;
  height: 30px;
  border: 1px solid var(--p-surface-200);
  border-radius: 999px;
  background: var(--p-surface-0);
  cursor: pointer;
  transition: background 0.15s ease;
}

.invest-drawer__close:hover {
  background: var(--p-surface-100);
}

.invest-drawer__close:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.invest-drawer__close-icon {
  font-size: 11px;
  color: var(--p-text-color);
}
</style>
